import React, { useState, useMemo } from 'react';
import { Modal, Field, Input, Select, Btn, Badge, SectionHeader } from '../components/UI';
import { fmtM, fmtDate, todayStr } from '../utils';
import { showToast } from '../components/UI';

export default function Prestamos({ store, setItem, delItem }) {
  const { movimientos } = store;
  const [modal, setModal] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [form, setForm] = useState({});
  const [cancelForm, setCancelForm] = useState({});

  const prestamos = useMemo(() =>
    Object.values(movimientos || {})
      .filter(m => m.tipo === 'prestamo')
      .sort((a, b) => (b.fecha || '').localeCompare(a.fecha || '')),
    [movimientos]);

  const pendientes = prestamos.filter(p => !p.cancelado);
  const cancelados = prestamos.filter(p => p.cancelado);
  const totalPendiente = pendientes.reduce((a, p) => a + (parseFloat(p.monto) || 0), 0);

  function openNew() {
    setForm({ monto: '', medio: 'efectivo', fecha: todayStr(), concepto: '' });
    setModal(true);
  }

  function save() {
    const monto = parseFloat(form.monto);
    if (!monto || !form.concepto) { showToast('Ingresá monto y concepto'); return; }
    const id = 'p' + Date.now();
    setItem('movimientos', id, {
      id, tipo: 'prestamo', fecha: form.fecha,
      hora: new Date().toTimeString().slice(0, 5),
      monto, medio: form.medio, signo: '-',
      concepto: form.concepto,
      desc: 'Préstamo: ' + form.concepto,
      cancelado: false, autor: 'panel'
    });
    showToast('Préstamo registrado — desconta de caja');
    setModal(null);
  }

  function openCancel(prestamo) {
    setCancelForm({ medio: 'efectivo', fecha: todayStr(), obs: '' });
    setCancelModal(prestamo);
  }

  function saveCancel() {
    const p = cancelModal;
    // Mark as cancelled
    setItem('movimientos', p.id, { ...p, cancelado: true, fechaCancelacion: cancelForm.fecha, medioCancelacion: cancelForm.medio, obsCancelacion: cancelForm.obs });
    // Register income
    const ingresoId = 'm' + Date.now();
    setItem('movimientos', ingresoId, {
      id: ingresoId, tipo: 'ingreso_prestamo', fecha: cancelForm.fecha,
      hora: new Date().toTimeString().slice(0, 5),
      monto: p.monto, medio: cancelForm.medio, signo: '+',
      desc: 'Cobro préstamo: ' + p.concepto,
      prestamoId: p.id, autor: 'panel'
    });
    showToast(`Préstamo cobrado — ${fmtM(p.monto)} ingresó a caja`);
    setCancelModal(null);
  }

  return (
    <div>
      {/* Summary */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'stretch' }}>
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px', flex: 1 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Pendiente de cobro</div>
          <div style={{ fontSize: 26, fontWeight: 700, fontFamily: 'Courier, monospace', color: totalPendiente > 0 ? 'var(--red)' : 'var(--tx3)' }}>{fmtM(totalPendiente)}</div>
          <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 3 }}>{pendientes.length} préstamo{pendientes.length !== 1 ? 's' : ''} activo{pendientes.length !== 1 ? 's' : ''}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Btn variant="grn" onClick={openNew}>+ Nuevo préstamo</Btn>
        </div>
      </div>

      {/* Pendientes */}
      <SectionHeader title="Pendientes de cobro" />
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden', marginBottom: 20 }}>
        {pendientes.length === 0
          ? <div style={{ textAlign: 'center', padding: 36, color: 'var(--tx3)', fontSize: 13 }}>Sin préstamos pendientes</div>
          : pendientes.map(p => {
            const diasPendiente = Math.floor((new Date() - new Date(p.fecha)) / 86400000);
            return (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border)', background: diasPendiente > 7 ? 'var(--yel-l)' : '' }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--red-l)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 18 }}>💊</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{p.concepto}</div>
                  <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>
                    {fmtDate(p.fecha)} · {p.medio === 'efectivo' ? 'Efectivo' : 'Transferencia'}
                    {diasPendiente > 7 && <span style={{ color: 'var(--yel)', fontWeight: 700, marginLeft: 8 }}>⚠ {diasPendiente}d pendiente</span>}
                  </div>
                </div>
                <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 15, color: 'var(--red)', marginRight: 8 }}>{fmtM(p.monto)}</div>
                <Btn size="sm" variant="grn" onClick={() => openCancel(p)}>Cobrar</Btn>
                <Btn size="sm" variant="red" onClick={() => { delItem('movimientos', p.id); showToast('Eliminado'); }}>×</Btn>
              </div>
            );
          })}
      </div>

      {/* Cancelados */}
      {cancelados.length > 0 && (
        <>
          <SectionHeader title="Historial cobrados" />
          <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
            {cancelados.slice(0, 10).map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx2)' }}>{p.concepto}</div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)' }}>
                    Salida: {fmtDate(p.fecha)} → Cobro: {fmtDate(p.fechaCancelacion)} · {p.medioCancelacion === 'efectivo' ? 'Efectivo' : 'Transferencia'}
                  </div>
                </div>
                <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 13, color: 'var(--grn)' }}>{fmtM(p.monto)}</div>
                <Badge type="grn">Cobrado</Badge>
              </div>
            ))}
          </div>
        </>
      )}

      {/* New prestamo modal */}
      {modal && (
        <Modal title="Nuevo préstamo / favor" onClose={() => setModal(null)} onSave={save} saveLabel="Registrar salida">
          <div style={{ background: 'var(--yel-l)', border: '1px solid #d4a843', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--yel)' }}>
            ⚠ Esta salida descuenta de caja inmediatamente. Quedará pendiente hasta que cobres.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
            <Field label="Concepto" full>
              <Input value={form.concepto} onChange={v => setForm(f => ({ ...f, concepto: v }))} placeholder="Ej: Medicamento para cliente Juan, medicamento X..." autoFocus />
            </Field>
            <Field label="Monto">
              <Input value={form.monto} onChange={v => setForm(f => ({ ...f, monto: v }))} type="number" placeholder="0" />
            </Field>
            <Field label="Fecha">
              <Input value={form.fecha} onChange={v => setForm(f => ({ ...f, fecha: v }))} type="date" />
            </Field>
            <Field label="Medio de salida" full>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['efectivo', 'Efectivo'], ['transferencia', 'Transferencia']].map(([val, label]) => (
                  <button key={val} onClick={() => setForm(f => ({ ...f, medio: val }))}
                    style={{ flex: 1, padding: 10, borderRadius: 8, border: `2px solid ${form.medio === val ? 'var(--grn)' : 'var(--border2)'}`, background: form.medio === val ? 'var(--grn-l)' : 'var(--surf2)', color: form.medio === val ? 'var(--grn)' : 'var(--tx2)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        </Modal>
      )}

      {/* Cancel modal */}
      {cancelModal && (
        <Modal title={`Cobrar préstamo — ${cancelModal.concepto}`} onClose={() => setCancelModal(null)} onSave={saveCancel} saveLabel="Confirmar cobro">
          <div style={{ background: 'var(--grn-l)', border: '1px solid #b0d9be', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--grn)', fontWeight: 600 }}>
            ✓ Al confirmar, {fmtM(cancelModal.monto)} vuelve a caja
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
            <Field label="Fecha de cobro">
              <Input value={cancelForm.fecha} onChange={v => setCancelForm(f => ({ ...f, fecha: v }))} type="date" />
            </Field>
            <Field label="Medio de cobro" full>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['efectivo', 'Efectivo'], ['transferencia', 'Transferencia']].map(([val, label]) => (
                  <button key={val} onClick={() => setCancelForm(f => ({ ...f, medio: val }))}
                    style={{ flex: 1, padding: 10, borderRadius: 8, border: `2px solid ${cancelForm.medio === val ? 'var(--grn)' : 'var(--border2)'}`, background: cancelForm.medio === val ? 'var(--grn-l)' : 'var(--surf2)', color: cancelForm.medio === val ? 'var(--grn)' : 'var(--tx2)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Observaciones" full>
              <Input value={cancelForm.obs} onChange={v => setCancelForm(f => ({ ...f, obs: v }))} placeholder="Opcional..." />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}
